import React from 'react';
import { Box, Button, Divider, Drawer, Typography } from '@mui/material';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import CompareArrowsRoundedIcon from '@mui/icons-material/CompareArrowsRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import KeyboardDoubleArrowLeftRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowLeftRounded';
import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded';
import ManageSearchRoundedIcon from '@mui/icons-material/ManageSearchRounded';
import SummarizeRoundedIcon from '@mui/icons-material/SummarizeRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import SpaRoundedIcon from '@mui/icons-material/SpaRounded';
import { NavLink, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import suiteLogo from '../../media/logo.png';
import './Sidebar.css';

const navigationItems = [
    {
        label: 'Inicio',
        icon: <DashboardRoundedIcon />,
        path: '/',
    },
    {
        label: 'Crear entradas',
        icon: <SpaRoundedIcon />,
        path: '/entradas',
    },
    {
        label: 'Consulta',
        icon: <ManageSearchRoundedIcon />,
        path: '/consulta-entradas',
    },
    {
        label: 'Almacenes',
        icon: <CompareArrowsRoundedIcon />,
        path: '/comparativa-almacenes',
    },
    {
        label: 'Variedades',
        icon: <CategoryRoundedIcon />,
        path: '/historico-variedad',
    },
    {
        label: 'Informes',
        icon: <SummarizeRoundedIcon />,
        path: '/informes',
    },
    // {
    //     label: 'Configuracion',
    //     icon: <SettingsRoundedIcon />,
    //     path: '/configuracion',
    // },
];

const isItemActive = (item, pathname) => {
    if (item.path === '/') {
        return pathname === '/';
    }

    return pathname === item.path ||
        pathname.startsWith(`${item.path}/`) ||
        Boolean(item.aliases?.includes(pathname));
};

const SidebarNavButton = ({ item, active, collapsed, onNavigate, activeLayoutId }) => (
    <Button
        component={NavLink}
        to={item.path}
        end={item.path === '/'}
        aria-current={active ? 'page' : undefined}
        className={`suite-nav-button suite-nav-button-muted${active ? ' suite-nav-button-active' : ''}`}
        startIcon={React.cloneElement(item.icon, { fontSize: 'small' })}
        onClick={onNavigate}
        title={collapsed ? item.label : undefined}
    >
        {active && (
            <Box
                component={motion.span}
                className="suite-nav-active-indicator"
                layoutId={activeLayoutId}
                transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            />
        )}
        <span className="suite-nav-label">{item.label}</span>
    </Button>
);

const SidebarSection = ({ label, items, collapsed, onNavigate, activeLayoutId, pathname }) => (
    <Box className="suite-sidebar-section">
        {!collapsed && (
            <Typography className="suite-sidebar-label">
                {label}
            </Typography>
        )}
        {items.map((item) => {
            const active = isItemActive(item, pathname);

            return (
                <SidebarNavButton
                    key={item.path}
                    item={item}
                    active={active}
                    collapsed={collapsed}
                    onNavigate={onNavigate}
                    activeLayoutId={activeLayoutId}
                />
            );
        })}
    </Box>
);

const SidebarInner = ({
    mobile,
    collapsed,
    onClose,
    onToggleCollapse,
    activeLayoutId,
}) => {
    const location = useLocation();
    const onNavigate = mobile ? onClose : undefined;

    return (
        <Box className={`suite-sidebar-content${mobile ? ' suite-sidebar-mobile' : ''}`}>
            <Box className="suite-brand" onClick={onToggleCollapse} sx={{ cursor: 'pointer'}}>
                <img className="suite-brand-logo" src={suiteLogo} alt="Clasol Suite Apps" />
            </Box>

            <Box>
                <SidebarSection
                    label="Clasol Suite - Flujos"
                    items={navigationItems}
                    collapsed={collapsed}
                    onNavigate={onNavigate}
                    activeLayoutId={activeLayoutId}
                    pathname={location.pathname}
                />
            </Box>

            <Box className="suite-sidebar-footer">
                <Typography className="suite-footer-copy">
                    © 2026 <strong>CLASOL SUITE APPS</strong>
                </Typography>
                <Typography className="suite-footer-version">
                    Version 0.0.1
                </Typography>

                {!mobile && (
                    <>
                        <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.12)' }} />
                        <Box
                            className="suite-collapse-row"
                            role="button"
                            tabIndex={0}
                            onClick={onToggleCollapse}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    onToggleCollapse?.();
                                }
                            }}
                        >
                            {collapsed ? <KeyboardDoubleArrowRightRoundedIcon fontSize="small" /> : <KeyboardDoubleArrowLeftRoundedIcon fontSize="small" />}
                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                                {collapsed ? 'Expandir' : 'Colapsar'}
                            </Typography>
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
};

export const Sidebar = ({
    open,
    mobile,
    collapsed,
    onClose,
    onToggleCollapse,
}) => {
    if (mobile) {
        return (
            <Drawer
                open={open}
                onClose={onClose}
                ModalProps={{ keepMounted: true }}
                slotProps={{
                    paper: {
                        className: 'suite-sidebar-drawer',
                    },
                }}
            >
                <SidebarInner
                    mobile
                    collapsed={false}
                    onClose={onClose}
                    onToggleCollapse={onToggleCollapse}
                    activeLayoutId="suite-active-nav-mobile"
                />
            </Drawer>
        );
    }

    return (
        <Box
            component="aside"
            className={`suite-sidebar${collapsed ? ' suite-sidebar-collapsed' : ''}`}
        >
            <SidebarInner
                mobile={false}
                collapsed={collapsed}
                onClose={onClose}
                onToggleCollapse={onToggleCollapse}
                activeLayoutId="suite-active-nav-desktop"
            />
        </Box>
    );
};
